import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
    LayoutDashboard, MessageSquare, Heart, Wind, CalendarCheck, LifeBuoy, UserCircle, LogOut, Leaf, Clock, Sparkles, BarChart3, Inbox, Lock
} from 'lucide-react';

const ADMIN_EMAILS = ['madsakjaeraas@gmail.com'];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const { t, lang } = useT();
    const navigate = useNavigate();
    const isAdmin = ADMIN_EMAILS.includes((user?.email || '').toLowerCase());

    const LINKS = [
        { to: '/app', label: t('nav.dashboard'), icon: LayoutDashboard, end: true, testid: 'nav-dashboard' },
        { to: '/app/samtaler', label: t('nav.chats'), icon: MessageSquare, testid: 'nav-chats' },
        { to: '/app/humor', label: t('nav.mood'), icon: Heart, testid: 'nav-mood' },
        { to: '/app/pust', label: t('nav.breathing'), icon: Wind, testid: 'nav-breathing' },
        { to: '/app/innsjekk', label: t('nav.checkin'), icon: CalendarCheck, testid: 'nav-checkin' },
        { to: '/app/ressurser', label: t('nav.resources'), icon: LifeBuoy, testid: 'nav-resources' },
        { to: '/app/konto', label: t('nav.settings'), icon: UserCircle, testid: 'nav-settings' },
    ];

    const ADMIN_LINKS = [
        { to: '/app/admin/analytics', label: t('nav.admin.analytics'), icon: BarChart3, testid: 'nav-admin-analytics' },
        { to: '/app/admin/tilbakemelding', label: t('nav.admin.feedback'), icon: Inbox, testid: 'nav-admin-feedback' },
    ];

    const brand = lang === 'en' ? 'Stillroom' : 'Stillerom';

    const doLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-bone text-ink flex flex-col md:flex-row md:h-screen">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 hidden md:flex flex-col border-r border-sand bg-white/50">
                <div className="px-5 py-6 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-bone" />
                    </div>
                    <span className="font-display font-bold tracking-tight">{brand}</span>
                </div>
                <nav className="px-3 flex-1 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
                    {LINKS.map(l => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? 'bg-sage text-bone' : 'text-inkSoft hover:bg-sand/40 hover:text-ink'}`
                            }
                        >
                            <l.icon className="w-4 h-4" /> {l.label}
                        </NavLink>
                    ))}
                    {isAdmin && (
                        <>
                            <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-inkSoft/70">{t('nav.admin')}</div>
                            {ADMIN_LINKS.map(l => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={l.testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? 'bg-sage text-bone' : 'text-inkSoft hover:bg-sand/40 hover:text-ink'}`
                                    }
                                >
                                    <l.icon className="w-4 h-4" /> {l.label}
                                </NavLink>
                            ))}
                        </>
                    )}
                </nav>
                <div className="p-3 border-t border-sand">
                    <div className="px-3 py-2 text-xs text-inkSoft" data-testid="user-alias">
                        {t('settings.signed.in.as')} <span className="font-medium text-ink">{user?.alias}</span>
                        {user?.plan
                            ? <span className="ml-2 text-sage-dark">{user.plan === 'basic' ? 'Basic' : 'Premium'}</span>
                            : <span className="ml-2">{user?.message_count}/{user?.free_limit} {lang === 'en' ? 'free' : 'gratis'}</span>
                        }
                    </div>
                    <button onClick={doLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-inkSoft hover:text-crisis" data-testid="logout-btn">
                        <LogOut className="w-4 h-4" /> {t('nav.logout')}
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden sticky top-0 z-40 bg-bone/95 backdrop-blur-sm border-b border-sand flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-3.5 h-3.5 text-bone" />
                    </div>
                    <span className="font-display font-bold text-sm">{brand}</span>
                </div>
                <div className="flex items-center gap-3">
                    {isAdmin && (
                        <NavLink
                            to="/app/admin/analytics"
                            className={({ isActive }) => `flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full transition-colors ${isActive ? 'bg-sage text-bone' : 'bg-sand/60 text-ink hover:bg-sand'}`}
                            data-testid="mobile-admin-link"
                            title={t('nav.admin')}
                        >
                            <BarChart3 className="w-3.5 h-3.5"/> Admin
                        </NavLink>
                    )}
                    <div className="text-xs text-inkSoft">
                        {user?.plan ? (user.plan === 'basic' ? 'Basic' : 'Premium') : `${user?.message_count}/${user?.free_limit}`}
                    </div>
                </div>
            </div>

            <main className="flex-1 overflow-y-auto flex flex-col">
                <TrialBanner user={user} onUpgrade={() => navigate('/app/konto')}/>
                <Outlet />

                {/* Footer with copyright + AI disclosure (visible on every in-app page) */}
                <footer className="border-t border-sand mt-12 pb-24 md:pb-8" data-testid="app-footer">
                    <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-inkSoft/80">
                        <p>© 2026 {brand}. {lang === 'en' ? 'All rights reserved.' : 'Alle rettigheter reservert.'}</p>
                        <div className="flex items-center gap-3">
                            <NavLink to="/personvern" className="hover:text-ink">{lang === 'en' ? 'Privacy' : 'Personvern'}</NavLink>
                            <span>·</span>
                            <NavLink to="/vilkar" className="hover:text-ink">{lang === 'en' ? 'Terms' : 'Vilkår'}</NavLink>
                            <span>·</span>
                            <span title={lang === 'en' ? 'You are talking to AI, not a human' : 'Du snakker med en KI, ikke et menneske'}>
                                {lang === 'en' ? 'AI assistant' : 'KI-assistent'}
                            </span>
                        </div>
                    </div>
                </footer>
                {/* Mobile bottom nav */}
                <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-bone/95 backdrop-blur-sm border-t border-sand grid grid-cols-5 z-50 pb-[env(safe-area-inset-bottom)]">
                    {LINKS.slice(0,5).map(l => (
                        <NavLink key={l.to} to={l.to} end={l.end} className={({isActive}) =>
                            `flex flex-col items-center gap-1 py-3 text-[10px] ${isActive ? 'text-sage-dark' : 'text-inkSoft'}`
                        } data-testid={`mobile-${l.testid}`}>
                            <l.icon className="w-5 h-5"/> {l.label}
                        </NavLink>
                    ))}
                </nav>
            </main>
            <ForcePasswordChangeModal user={user} />
        </div>
    );
}

function ForcePasswordChangeModal({ user }) {
    const { t } = useT();
    const { refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [next, setNext] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const open = !!user?.must_change_password;

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 6) { toast.error(t('password.too.short')); return; }
        if (next !== confirm) { toast.error(t('password.mismatch')); return; }
        setBusy(true);
        try {
            await api.post('/me/password', { new_password: next });
            toast.success(t('password.changed'));
            await refresh();
            setNext(''); setConfirm('');
        } catch (err) {
            toast.error(err.response?.data?.detail || t('password.change.error'));
        } finally { setBusy(false); }
    };

    const signOut = async () => {
        await logout();
        navigate('/');
    };

    return (
        <Dialog open={open} onOpenChange={() => { /* not dismissible */ }}>
            <DialogContent
                className="bg-bone max-w-md [&>button.absolute]:hidden"
                data-testid="force-password-modal"
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Lock className="w-4 h-4"/> {t('password.must.change.title')}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-inkSoft leading-relaxed mt-1">
                        {t('password.must.change.body')}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-2 mt-2">
                    <input
                        type="password"
                        required
                        minLength={6}
                        autoFocus
                        autoComplete="new-password"
                        placeholder={t('password.new')}
                        value={next}
                        onChange={e => setNext(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="force-password-new"
                    />
                    <input
                        type="password"
                        required
                        minLength={6}
                        autoComplete="new-password"
                        placeholder={t('password.confirm')}
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        className="w-full rounded-lg border border-sand bg-white px-3 py-2 text-sm focus:outline-none focus:border-sage"
                        data-testid="force-password-confirm"
                    />
                    <div className="flex gap-2 pt-1">
                        <Button type="submit" disabled={busy} className="rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="force-password-submit">
                            {busy ? t('password.busy') : t('password.submit')}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="rounded-full text-inkSoft" onClick={signOut} data-testid="force-password-logout">
                            {t('settings.logout')}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function TrialBanner({ user, onUpgrade }) {
    const { t, lang } = useT();
    if (!user?.on_trial) return null;
    const hoursLeft = user.trial_hours_left ?? 72;
    const lossAversion = hoursLeft <= 24;
    if (lossAversion) {
        const text = lang === 'en'
            ? <>Your conversation history will disappear in <strong>{Math.max(0, Math.round(hoursLeft))} hours</strong>. Keep it for $7/mo.</>
            : <>Samtalehistorikken din forsvinner om <strong>{Math.max(0, Math.round(hoursLeft))} timer</strong>. Behold den for 99 kr/mnd.</>;
        return (
            <div className="bg-crisis text-white px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="trial-banner-urgent">
                <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-4 h-4 shrink-0"/>
                    <span className="truncate">{text}</span>
                </div>
                <button onClick={onUpgrade} className="shrink-0 bg-white text-crisis text-xs font-medium px-3 py-1 rounded-full hover:bg-bone transition-colors" data-testid="trial-urgent-cta">
                    {lang === 'en' ? 'Keep' : 'Behold'}
                </button>
            </div>
        );
    }
    const labelTrial = lang === 'en' ? 'Free Premium' : 'Gratis Premium';
    const hoursLabel = lang === 'en' ? 'hours left' : 'timer igjen';
    const seePlans = lang === 'en' ? 'See plans' : 'Se planer';
    return (
        <div className="bg-sage-soft text-ink px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="trial-banner">
            <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 text-sage-dark shrink-0"/>
                <span className="truncate text-inkSoft">
                    {labelTrial} · <strong className="text-ink">{Math.max(0, Math.round(hoursLeft))} {hoursLabel}</strong>
                </span>
            </div>
            <button onClick={onUpgrade} className="shrink-0 text-xs font-medium text-sage-dark hover:underline" data-testid="trial-cta">
                {seePlans}
            </button>
        </div>
    );
}
